import { trpc } from '@/trpcClient';
import { DeleteMaterialInputType } from '@shelflix/schemas/material/delete';
import { ConfirmationModal } from '@shelflix/ui-web/components/ConfirmationModal';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowRightLeft, Building2, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { resolveReturnUrl } from '@shelflix/schemas/dashboard/drilldownUrl';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface DeleteSectionProps {
  materialUuid: string;
  facility: {
    uuid: string;
    name: string;
    address: string;
    city: {
      uuid: string;
      name: string;
      ibgeCode: string;
    };
    state: {
      uuid: string;
      name: string;
      ibgeCode: string;
      stateName: string;
    };
  };
}

export function DeleteSection({ materialUuid, facility }: DeleteSectionProps) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const deleteMaterialMutation = useMutation(trpc.material.delete.mutationOptions());
  const navigate = useNavigate();
  const { state } = useLocation();
  const form = useForm<DeleteMaterialInputType>({
    defaultValues: {
      materialUuid: materialUuid,
    },
    mode: 'all',
  });

  const handleDeleteClick = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteMaterialMutation.isPending) return;
    const data = form.getValues();
    deleteMaterialMutation.mutate(data, {
      onSuccess: () => {
        form.reset();
        setShowConfirmationModal(false);
        toast.success('Material removido com sucesso!', {
          description: 'O material e todos os dados relacionados foram removidos do sistema.',
        });
        navigate(resolveReturnUrl(state?.from, '/pos'));
      },
      onError: (error) => {
        toast.error(`Erro ao deletar material: ${error.message}`);
        setShowConfirmationModal(false);
      },
    });
  };

  const handleCancelDelete = () => {
    setShowConfirmationModal(false);
  };

  return (
    <Card className="rounded-lg border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
          <ArrowRightLeft className="h-5 w-5" />
          Remover Material
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6 rounded-lg bg-[var(--background)] p-4">
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 text-gray-500" />
            <div>
              <h4 className="font-medium">Estabelecimento Atual</h4>
              <div className="mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {facility.name} - {facility.city.name} / {facility.state.name}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-lg border border-red-800 bg-[var(--background)] p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <h4 className="font-medium">Atenção</h4>
                <ul className="mt-1 space-y-1 text-sm">
                  <li>• Esta operação removerá o material do estabelecimento</li>
                  <li>• O material será removido do estabelecimento atual e não poderá ser recuperado</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              type="button"
              onClick={handleDeleteClick}
              loading={deleteMaterialMutation.isPending}
              disabled={deleteMaterialMutation.isPending}
              className="bg-red-500 px-6 hover:bg-red-500"
            >
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              {deleteMaterialMutation.isPending ? 'Removendo...' : 'Remover Material'}
            </Button>
          </div>
        </div>

        <ConfirmationModal
          open={showConfirmationModal}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Confirmar Remoção do Material"
          description={`Tem certeza que deseja remover este material do estabelecimento "${facility.name}"? Esta ação não pode ser desfeita.`}
          confirmButtonVariant="destructive"
          loading={deleteMaterialMutation.isPending}
          className="border-red-500"
        />
      </CardContent>
    </Card>
  );
}
